import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "How Attackers Trick People".
 *
 * Most phishing exercises ask you to look at a message and feel
 * suspicious. Feeling suspicious is not a skill - it fails on the
 * convincing ones and cries wolf on the real ones.
 *
 * So this inbox makes you check things instead. The display name and
 * the actual address are separate, links show their true destination
 * when you inspect them, and three of the eight messages are completely
 * genuine - including one that looks alarming. A learner who marks
 * everything as phishing scores five out of eight and bins three real
 * messages, which is the point.
 *
 * The pressure techniques from the lesson are named on the reveal, so
 * the vocabulary attaches to something concrete.
 */

const STORE_KEY = 'cybercub.lab.inbox';

const MAIL = [
  {
    id: 'm1',
    from: 'Marbury Tutors IT',
    address: 'it-helpdesk@marburytutors-support.co.uk',
    subject: 'Mailbox full — action required within 24 hours',
    when: '09:12',
    body: [
      'Your mailbox has reached its storage limit and incoming messages are being rejected.',
      'Verify your account within 24 hours to avoid permanent deletion of stored mail.',
    ],
    links: [{ text: 'Verify my mailbox', real: 'https://marburytutors-support.co.uk/login/verify' }],
    phish: true,
    tells: ['urgency', 'lookalike', 'threat'],
    why:
      'The address is the giveaway. The real company is marburytutors.co.uk; this is marburytutors-support.co.uk, which is a different domain somebody registered. The 24-hour deadline is there to stop you checking.',
  },
  {
    id: 'm2',
    from: 'Rachel Okonkwo',
    address: 'r.okonkwo@marburytutors.co.uk',
    subject: 'Thursday’s staff meeting moved to 3pm',
    when: '09:40',
    body: [
      'Morning all — moving Thursday to 3pm as the hall is booked. Same agenda.',
      'No need to reply unless that clashes for you.',
    ],
    links: [],
    phish: false,
    why:
      'Real address, ordinary request, nothing to click, nothing to do in a hurry. Most email is like this, which is exactly why the rare bad one works.',
  },
  {
    id: 'm3',
    from: 'Rachel Okonkwo',
    address: 'r.okonkwo@marbury-tutors.co.uk',
    subject: 'Quick favour — are you at your desk?',
    when: '10:02',
    body: [
      'I’m in back-to-back meetings and can’t take calls. I need you to sort out a payment for a supplier before 2pm.',
      'Can you confirm you’re free? I’ll send the details straight over. Please keep this between us for now — it’s a sensitive contract.',
    ],
    links: [],
    phish: true,
    tells: ['authority', 'isolation', 'urgency'],
    why:
      'Look very closely at the address: marbury-tutors.co.uk, with a hyphen. The real one has none. Everything else is technique — the boss, the deadline, no way to phone her, and "keep this between us", which exists only to stop you asking anybody.',
  },
  {
    id: 'm4',
    from: 'Clearpay Systems',
    address: 'no-reply@clearpay-systems.net',
    subject: 'Unusual sign-in blocked — confirm it was you',
    when: '11:18',
    body: [
      'We blocked a sign-in to your Clearpay account from a device in Lagos, Nigeria.',
      'If this was not you, secure your account immediately using the link below.',
    ],
    links: [{ text: 'Secure my account', real: 'http://185.62.190.11/clearpay/secure' }],
    phish: true,
    tells: ['fear', 'urgency', 'lookalike'],
    why:
      'The link does not go to a name at all — it goes to a bare address on the internet. A real payment company never sends you to a number. The foreign city is there to frighten you into clicking before you look.',
  },
  {
    id: 'm5',
    from: 'Priya Nair',
    address: 'p.nair@marburytutors.co.uk',
    subject: 'Printer code?',
    when: '11:55',
    body: [
      'Sorry — what’s the code for the printer by the kitchen? Tom’s out and I’ve got a stack to do.',
    ],
    links: [],
    phish: false,
    why:
      'Genuine address, believable request, and a new starter asking a small question. Treating this as an attack is its own kind of failure — it makes people stop asking, and a workplace where nobody asks is easier to attack, not harder.',
  },
  {
    id: 'm6',
    from: 'HR Department',
    address: 'hr@marburytutors.co.uk.payroll-review.com',
    subject: 'Your salary review document',
    when: '13:30',
    body: [
      'Please review the attached salary banding document before Friday.',
      'You will need to sign in with your work account to open it.',
    ],
    links: [{ text: 'Open salary_review_2026.pdf', real: 'https://payroll-review.com/o365/login' }],
    phish: true,
    tells: ['curiosity', 'lookalike'],
    why:
      'Read the address right to left. The real domain is the last part before the slash — payroll-review.com. Everything in front of it, including marburytutors.co.uk, is just words somebody chose. Salary is bait precisely because you want to look.',
  },
  {
    id: 'm7',
    from: 'Marbury Tutors IT',
    address: 'it@marburytutors.co.uk',
    subject: 'We are switching on two-step sign-in next week',
    when: '14:05',
    body: [
      'From Monday everyone will be asked for a code from their phone as well as a password.',
      'We will never ask you for that code. If anybody rings and asks you to read one out, that call is not from us — put the phone down and tell Rachel.',
    ],
    links: [],
    phish: false,
    why:
      'Real address and, more importantly, it tells you what it will never do. Genuine security messages hand you a rule you can check them against later. Attackers avoid that, because it makes their next move harder.',
  },
  {
    id: 'm8',
    from: 'Microsoft 365',
    address: 'security@micros0ft-account.com',
    subject: 'Your password expires today',
    when: '15:41',
    body: [
      'Your password will expire in 4 hours. Keep your current password by confirming your identity.',
    ],
    links: [{ text: 'Keep my current password', real: 'https://micros0ft-account.com/verify' }],
    phish: true,
    tells: ['urgency', 'lookalike'],
    why:
      'A zero in place of the o. Read carefully, it is obvious; read at speed while doing something else, it is invisible — and it is designed to be read at speed. "Keep your current password" is also the wrong way round, because it makes doing nothing feel like the risky choice.',
  },
];

const TELL_NAMES = {
  urgency: 'Urgency — a deadline that exists to stop you checking',
  authority: 'Authority — someone senior, so you will not push back',
  fear: 'Fear — something frightening, so you act before thinking',
  curiosity: 'Curiosity — something you want to look at',
  isolation: 'Isolation — "keep this between us", so you ask nobody',
  threat: 'Threat — something will be lost if you do not move',
  lookalike: 'A lookalike address — nearly right, and not right',
};

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

export default function InboxLab() {
  const saved = useMemo(load, []);

  const [open, setOpen] = useState('m1');
  const [picks, setPicks] = useState(saved.picks ?? {});
  const [inspected, setInspected] = useState([]);

  useEffect(() => { document.title = 'The two checks · CyberCub Lab'; }, []);
  useEffect(() => { save({ picks }); }, [picks]);

  const mail = MAIL.find((m) => m.id === open);
  const judged = MAIL.filter((m) => picks[m.id] !== undefined);
  const right = MAIL.filter((m) => picks[m.id] === m.phish).length;
  const allDone = judged.length === MAIL.length;

  useFinished('/lab/inbox', allDone);
  const perfect = right === MAIL.length;

  // How many genuine messages were wrongly binned. Worth naming: an
  // inbox where everything is suspicious is its own failure.
  const falseAlarms = MAIL.filter((m) => !m.phish && picks[m.id] === true).length;

  const judge = (id, asPhish) => setPicks((prev) => ({ ...prev, [id]: asPhish }));
  const showReal = inspected.includes(open);

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>The two checks</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 1</span>
          </div>
        </div>
        <Link to="/learn/lesson/eh1l4-how-attackers-trick-people" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <section className="lab-section">
        <div className="place-head">
          <div>
            <h2>One morning at Marbury Tutors</h2>
            <p className="lab-lead">
              Eight messages. Check each one properly — the real sender address, and where a link
              actually goes — then decide. Not all of them are attacks, and marking everything as
              one is its own kind of wrong.
            </p>
          </div>
          <span className="pill">{judged.length} / {MAIL.length} judged</span>
        </div>

        <Tutor
          avatar="cub-teal"
          say={
            allDone
              ? perfect
                ? 'Eight out of eight, including all three genuine ones. That second part is the bit people miss: an office where every message is treated as an attack stops working, and stops telling anybody when something is actually wrong.'
                : `${right} out of eight. Go back through the ones you got wrong and look only at the address — right to left, last part before the slash. That single habit catches most of these.`
              : 'Do not ask yourself whether it feels suspicious. Ask two questions you can answer: what is the actual address it came from, and where does the link really go? Feelings miss the good ones.'
          }
        />

        <div className="inbox">
          {/* ---------------- the list ---------------- */}
          <ul className="inbox-list" aria-label="Messages">
            {MAIL.map((m) => {
              const pick = picks[m.id];
              const done = pick !== undefined;
              const correct = pick === m.phish;
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    className={`inbox-row ${open === m.id ? 'is-open' : ''} ${done ? (correct ? 'is-right' : 'is-wrong') : ''}`}
                    onClick={() => setOpen(m.id)}
                  >
                    <span className="inbox-from">{m.from}</span>
                    <span className="inbox-subject">{m.subject}</span>
                    <span className="inbox-when">{done ? (correct ? '✓' : '✗') : m.when}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* ---------------- the message ---------------- */}
          <div className="inbox-reader">
            <h3 className="inbox-head">{mail.subject}</h3>

            <div className="inbox-sender">
              <span className="inbox-avatar" aria-hidden="true">{mail.from.slice(0, 1)}</span>
              <div>
                <strong>{mail.from}</strong>
                <code className="inbox-address">&lt;{mail.address}&gt;</code>
              </div>
              <span className="inbox-time">{mail.when}</span>
            </div>

            <div className="inbox-body">
              {mail.body.map((p, i) => <p key={i}>{p}</p>)}

              {mail.links.map((l) => (
                <p key={l.text} className="inbox-linkline">
                  <span className="inbox-link">{l.text}</span>
                  {showReal ? (
                    <code className="inbox-real">→ {l.real}</code>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() => setInspected((v) => [...v, mail.id])}
                    >
                      Where does this go?
                    </button>
                  )}
                </p>
              ))}

              {mail.links.length === 0 && (
                <p className="inbox-nolink">No links in this message.</p>
              )}
            </div>

            {picks[mail.id] === undefined ? (
              <div className="inbox-judge">
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => judge(mail.id, false)}>
                  ✅ Genuine
                </button>
                <button type="button" className="btn btn-sm" onClick={() => judge(mail.id, true)}>
                  🎣 Phishing
                </button>
              </div>
            ) : (
              <div className={`inbox-verdict ${picks[mail.id] === mail.phish ? 'is-right' : 'is-wrong'}`}>
                <strong>
                  {picks[mail.id] === mail.phish ? '✓ ' : '✗ '}
                  {mail.phish ? 'This one is phishing.' : 'This one is genuine.'}
                </strong>
                <p>{mail.why}</p>
                {mail.tells && (
                  <ul className="inbox-tells">
                    {mail.tells.map((t) => <li key={t}>{TELL_NAMES[t]}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {allDone && (
          <div className="lab-done">
            <div>
              <strong>{right} out of {MAIL.length}.</strong>
              <p style={{ margin: '0.3rem 0 0' }}>
                {falseAlarms > 0
                  ? `You binned ${falseAlarms} genuine message${falseAlarms > 1 ? 's' : ''}. That matters as much as missing a bad one: the office that cannot tell the difference stops trusting its own email, and the person who was told off for asking about the printer will not ask next time either.`
                  : 'Including all three genuine ones. The habit that got you there was not suspicion — it was reading the address right to left, and checking where a link actually went before believing the words on top of it.'}
              </p>
            </div>
            <Link to="/learn/lesson/eh1l4-how-attackers-trick-people" className="btn btn-sm">
              ← Back to the lesson
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
